import powurLogoWhite from "~/assets/svg/powur-logo-white.svg?url"

import { Eyebrow, Icon } from "../../BatteryOnlyProposal/primitives"
import { useActiveSection } from "../../BatteryOnlyProposal/use-active-section"
import { formatCreatedDate } from "../../BatteryOnlyProposal/view-helpers"
import type {
  SolarHeadline,
  SolarPerson,
  SolarPresentationProposal,
} from "../solar-presentation"

const formatAddress = ({ address }: SolarHeadline): string =>
  `${address.street}, ${address.city}, ${address.state} ${address.postal}`

const personContact = (person: SolarPerson): string =>
  [person.email, person.phone].filter(Boolean).join(` · `)

/** Wordmark, the people on the proposal, and its identifier in one band.
 * Not sticky, matching the Battery header: SectionRail carries wayfinding. */
export function SolarHeader({
  headline,
}: {
  headline: SolarHeadline
}): JSX.Element {
  const address = formatAddress(headline)
  const consultantLine = personContact(headline.consultant)

  return (
    <header className="border-b border-bp-border bg-bp-base px-6 py-5 lg:px-16">
      <div className="flex items-center gap-4">
        <img src={powurLogoWhite} alt="Powur" className="h-[26px] w-auto" />
        <div className="h-[22px] w-px bg-bp-border-strong" />
        <span className="text-[length:var(--step--2)] font-semibold tracking-[0.18em] text-bp-text-dim uppercase">
          Solar Proposal
        </span>
      </div>
      <div className="mt-7 grid items-center gap-8 md:grid-cols-[1fr_1fr_auto] md:gap-12">
        <div className="min-w-0">
          <Eyebrow className="mb-1">Prepared for</Eyebrow>
          <div
            className="font-display truncate text-[length:var(--step-1)] leading-tight font-semibold tracking-[-0.015em] text-bp-text"
            title={headline.homeowner.name}
          >
            {headline.homeowner.name}
          </div>
          <div className="mt-1 flex items-center gap-1.5 text-[length:var(--step--2)] text-bp-text-muted">
            <Icon name="pin" className="size-3 text-bp-text-dim" />
            <span className="truncate" title={address}>
              {address}
            </span>
          </div>
        </div>
        <div className="min-w-0">
          <Eyebrow className="mb-1">Prepared by your consultant</Eyebrow>
          <div
            className="font-display truncate text-[length:var(--step-1)] leading-tight font-semibold tracking-[-0.015em] text-bp-text"
            title={headline.consultant.name}
          >
            {headline.consultant.name}
          </div>
          {consultantLine ? (
            <div
              className="mt-1 truncate text-[length:var(--step--2)] text-bp-text-muted"
              title={consultantLine}
            >
              {consultantLine}
            </div>
          ) : null}
        </div>
        {headline.proposalNumber ? (
          <div className="flex flex-col gap-1.5 md:items-end">
            <Eyebrow>Proposal</Eyebrow>
            <div className="w-fit rounded-full border border-bp-border bg-bp-glass-hi px-3 py-1 font-mono text-[length:var(--step--2)] font-semibold tracking-wide text-bp-text-soft">
              {headline.proposalNumber}
            </div>
          </div>
        ) : null}
      </div>
    </header>
  )
}

/** Desktop-only dot rail pinned to the right edge, as on the Battery page.
 * "Top" leads, then one dot per section the page actually renders. A real
 * proposal ends on its last section with no footer below, so the last dot
 * lights once the page is scrolled to the bottom. */
export function SectionRail({
  sections,
  observeKey,
  hidden = false,
  staleSections,
}: {
  sections: readonly { id: string; label: string }[]
  /** Changes when a section's node is replaced under the same anchor. */
  observeKey?: string
  /** Steps aside while the seller's Updates panel covers the right edge. */
  hidden?: boolean
  /** Sections whose figures are out of date: a gold badge on their dot. */
  staleSections?: ReadonlySet<string>
}): JSX.Element {
  const links = [{ id: `hero`, label: `Top` }, ...sections]
  const active = useActiveSection(
    links.map((link) => link.id),
    { lastAtPageEnd: true, observeKey },
  )

  return (
    <nav
      aria-label="Sections"
      className={`fixed top-1/2 right-5 z-20 hidden -translate-y-1/2 flex-col gap-2.5 transition-opacity lg:flex print:hidden ${
        hidden ? `pointer-events-none invisible opacity-0` : ``
      }`}
    >
      {links.map((link) => {
        const stale = staleSections?.has(link.id) === true
        return (
          <a
            key={link.id}
            href={`#${link.id}`}
            data-stale={stale || undefined}
            aria-current={active === link.id ? `location` : undefined}
            className="group flex items-center justify-end gap-2.5 focus-visible:ring-2 focus-visible:ring-bp-accent focus-visible:outline-none"
          >
            <span
              className={`rounded-full border border-bp-border bg-bp-page px-2.5 py-0.5 text-[length:var(--step--2)] font-semibold whitespace-nowrap opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 ${
                active === link.id ? `text-bp-text` : `text-bp-text-muted`
              }`}
            >
              {link.label}
              {stale ? ` · out of date` : null}
            </span>
            <span
              className={`relative size-2.5 shrink-0 rounded-full border transition-transform ${
                active === link.id
                  ? `scale-125 border-bp-accent bg-bp-accent`
                  : `border-bp-border-strong bg-bp-surface group-hover:border-bp-accent`
              }`}
            >
              {stale ? (
                <span
                  aria-hidden="true"
                  className="absolute -top-[5px] -right-[5px] size-[7px] rounded-full bg-bp-gold ring-2 ring-bp-page"
                />
              ) : null}
            </span>
          </a>
        )
      })}
    </nav>
  )
}

export function SolarFooter({
  proposal,
}: {
  proposal: SolarPresentationProposal
}): JSX.Element {
  const created = formatCreatedDate(proposal.createdAt)

  return (
    <footer className="flex flex-wrap items-center justify-between gap-4 border-t border-bp-border px-6 pt-8 pb-8 lg:px-16">
      <img src={powurLogoWhite} alt="Powur" className="h-7 w-auto" />
      <div className="text-[length:var(--step--2)] tracking-wide text-bp-text-dim">
        © {new Date(proposal.createdAt).getUTCFullYear()} Powur · Proposal{` `}
        {proposal.id}
        {created ? ` · ${created}` : ``}
      </div>
    </footer>
  )
}
